import React, { useEffect, useState } from 'react'
import { T } from '../../../traks'
import { Container, Row, Col } from 'reactstrap'
import { SummaryRow } from './SummaryRow'
import { determineExtraOptionName, determinePackageDescription, determinePackageName } from './CartUtils'
import { formatDate, formatPrice } from '../Globals/GlobalUtils'
import { ExtraOptions } from '../Package/ExtraOptions'
import { calculatePackagePrice } from '../Package/PackageUtils'
import { calculateExtensionTotal, determineExpirationDate } from '../Order/OrderUtils'

export const CartPackageItem = ({ line, lineIndex, cart, setCart, vatRate, setView, defaultCurrency, handleRemove }) => {
  const [selectedExtraOption, setSelectedExtraOption] = useState(line.extraOptionId || null)

  useEffect(() => {
    setSelectedExtraOption(line.extraOptionId || null)
  }, [line.extraOptionId])

  const selectedPackage = line.packageId
  const packageKey = selectedPackage != null ? String(selectedPackage) : null

  const cartData = cart || {}
  const packageDetails = cartData.package_details || {}
  const extraOptionsMonths = cartData.extra_options_months || {}
  const packages = cartData.packages || {}
  const packagesNoDiscount = cartData.packages_no_discount || {}
  const perKeyPrice = cartData.per_key_price || 0
  const perKeyPriceNoDiscount = cartData.per_key_price_no_discount || 0

  const currentExtraMonths = line.extraOptionId ? extraOptionsMonths[line.extraOptionId] || 0 : 0
  const basePackageMonths =
    packageKey && packageDetails[packageKey] && packageDetails[packageKey].numMonths
      ? packageDetails[packageKey].numMonths
      : Math.max(1, (line.months || 0) - currentExtraMonths)
  const keysFromDetails =
    packageKey && packageDetails[packageKey] && packageDetails[packageKey].numKeys ? packageDetails[packageKey].numKeys : line.keys

  const extra5Months = extraOptionsMonths['1'] || 5
  const extra11Months = extraOptionsMonths['2'] || 11

  const extensionTotal5 = calculateExtensionTotal(extra5Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys: keysFromDetails,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: true,
  })

  const extensionTotal11 = calculateExtensionTotal(extra11Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys: keysFromDetails,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: true,
  })

  const extensionNoDiscountTotal5 = calculateExtensionTotal(extra5Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys: keysFromDetails,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: false,
  })

  const extensionNoDiscountTotal11 = calculateExtensionTotal(extra11Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys: keysFromDetails,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: false,
  })

  const { packageTotal, packageTotalNoVat } = calculatePackagePrice({
    selectedPackage,
    packages,
    vatRate,
  })

  const computeFinalTotals = (extraOptionId) => {
    const vatFactor = 1 + (vatRate || 0) / 100
    if (extraOptionId === '1') {
      return {
        finalTotal: packageTotal + extensionTotal5,
        finalTotalNoVat: packageTotalNoVat + extensionTotal5 / vatFactor,
      }
    }
    if (extraOptionId === '2') {
      return {
        finalTotal: packageTotal + extensionTotal11,
        finalTotalNoVat: packageTotalNoVat + extensionTotal11 / vatFactor,
      }
    }
    return {
      finalTotal: packageTotal,
      finalTotalNoVat: packageTotalNoVat,
    }
  }

  const handleExtraOptionChange = (nextOption) => {
    setSelectedExtraOption(nextOption)
    if (!setCart) return

    const extraMonths = nextOption ? extraOptionsMonths[nextOption] || 0 : 0
    const totalMonths = basePackageMonths + extraMonths
    const { finalTotal, finalTotalNoVat } = computeFinalTotals(nextOption)
    const expirationDate = determineExpirationDate(line.startDate, totalMonths)

    const updatedLine = {
      ...line,
      extraOptionId: nextOption,
      keys: keysFromDetails,
      months: totalMonths,
      discountedTotal: finalTotal,
      discountedTotalNoVat: finalTotalNoVat,
      expirationDate: new Date(expirationDate).toISOString().split('T')[0],
    }

    setCart((prev) => {
      const prevLines = Array.isArray(prev.lines) ? prev.lines : []
      const targetIndex = Number.isInteger(lineIndex) ? lineIndex : 0
      const nextLines = prevLines.length ? prevLines.map((item, idx) => (idx === targetIndex ? updatedLine : item)) : [updatedLine]
      return { ...prev, lines: nextLines }
    })
  }

  const showExtraOptions =
    selectedPackage &&
    Object.keys(extraOptionsMonths || {}).length > 0 &&
    Object.keys(packageDetails || {}).length > 0 &&
    Object.keys(packages || {}).length > 0

  return (
    <React.Fragment>
      <h4 className="mb-4">
        <T>Thank you for choosing Aktiedysten</T>
      </h4>

      <Container>
        <Row className="justify-content-between">
          <Col lg="8" md="8" sm="8">
            <SummaryRow
              icon="https://static.aktiedysten.dk/gfx/present-icon-pink-29-07-2025.png"
              label={<T>Package choice</T>}
              value={1}
              bold={true}
              extraDetails={determinePackageName(line.packageId)}
              itemDescription={determinePackageDescription(line.packageId)}
            />

            <SummaryRow icon="https://static.aktiedysten.dk/gfx/user-icon-green-2023-0.svg" label={<T>Invitations</T>} value={line.keys} bold={true} />

            <SummaryRow icon="/gfx/calendar-color-0.svg" label={<T>Months</T>} value={line.months} bold={true} />

            <SummaryRow
              icon="/gfx/calendar-color-0.svg"
              label={<T>Add‑ons</T>}
              value={1}
              bold={true}
              extraDetails={determineExtraOptionName(selectedExtraOption)}
            />

            <SummaryRow icon="/gfx/calendar-color-0.svg" label={<T>Start date</T>} value={formatDate(new Date(line.startDate))} bold={true} />

            <SummaryRow icon="/gfx/calendar-color-0.svg" label={<T>Premium valid until</T>} value={formatDate(new Date(line.expirationDate))} bold={true} />

            {showExtraOptions && (
              <Row className="mb-3 mt-3">
                <ExtraOptions
                    showTitle={false}
                  selectedExtraOption={selectedExtraOption}
                  setSelectedExtraOption={handleExtraOptionChange}
                  extensionTotal5={extensionTotal5}
                  extensionTotal11={extensionTotal11}
                />
              </Row>
            )}

            <div className="mt-2">
              <b>
                <T>Price:</T>&nbsp;
              </b>
              <span>
                {formatPrice(line.discountedTotal)} {defaultCurrency}&nbsp;
              </span>
              <span className="checkout-price-no-vat">
                ({formatPrice(line.discountedTotalNoVat)} {defaultCurrency}
                &nbsp;<T>excl. VAT</T>)
              </span>
            </div>
          </Col>

          <Col xs="auto" className="text-right">
            <Row className="mb-2">
              <a role="button" className="mr-2" onClick={() => setView('order')}>
                <img className="cart-icon" src="/gfx/gear-icon-0.svg" alt="Edit" />
                <T>Edit</T>
              </a>
            </Row>
            <Row>
              <a role="button" className="mr-2" onClick={handleRemove}>
                <img className="cart-icon" src="/gfx/remove-portfolio-icon-1.svg" alt="Remove" />
                <T>Remove</T>
              </a>
            </Row>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/c/PremiumKeys/Cart/CartPackageItem.js